import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone } from "lucide-react";
import { api, fileUrl, formatApiError } from "../../lib/api";
import { STATUS_COLORS } from "../../lib/status";
import { PaymentTools, ShopDetails } from "./PaymentTools";

export const StatusBadge = ({ s }) => <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[s]}`}>{s}</span>;

const Detail = ({ order, statuses, onSaved }) => {
  const [notes, setNotes] = useState(order.notes);
  useEffect(() => { setNotes(order.notes); }, [order]);
  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, body);
      onSaved(data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const row = (k, v) => v ? <div><dt className="text-xs font-semibold uppercase tracking-wider text-[#D4A537]">{k}</dt><dd className="mt-0.5 whitespace-pre-wrap">{v}</dd></div> : null;
  return (
    <div className="rounded-3xl bg-white p-6 ring-1 ring-[#EBD08A]" data-testid="admin-order-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold" data-testid="admin-order-detail-name">{order.name}</h2>
          <p className="text-sm text-black/50">{new Date(order.created_at).toLocaleString()}</p>
        </div>
        <select value={order.status} onChange={(e) => patch({ status: e.target.value })} className="rounded-full border border-[#EBD08A] px-4 py-2 text-sm font-semibold" data-testid="admin-order-status-select">
          {statuses.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <a href={`tel:${order.phone}`} className="flex items-center gap-1.5 rounded-full bg-[#FFF4F8] px-4 py-2 text-sm font-semibold" data-testid="admin-order-call"><Phone className="h-4 w-4 text-[#FF1F8E]" />{order.phone}</a>
        <a href={`mailto:${order.email}`} className="flex items-center gap-1.5 rounded-full bg-[#FFF4F8] px-4 py-2 text-sm font-semibold" data-testid="admin-order-email"><Mail className="h-4 w-4 text-[#FF1F8E]" />{order.email}</a>
      </div>
      <dl className="mt-6 grid gap-4 sm:grid-cols-2">
        {row("Product", order.product_type)}{row("Quantity", order.quantity)}
        {row("Needed by", order.needed_by)}{row("Payment", order.paid_via ? `${order.paid_via} (customer chose ${order.payment_method})` : order.payment_method)}
        <div className="sm:col-span-2">{row("Sizes & colors", order.sizes_colors)}</div>
        <div className="sm:col-span-2">{row("Design idea", order.design_idea)}</div>
      </dl>
      {order.reference_images.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-3" data-testid="admin-order-images">
          {order.reference_images.map((p) => (
            <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} alt="Reference" className="h-28 w-28 rounded-xl object-cover ring-1 ring-[#EBD08A]" /></a>
          ))}
        </div>
      )}
      <ShopDetails order={order} />
      <PaymentTools order={order} onSaved={onSaved} />
      <div className="mt-6">
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-[#D4A537]">Private notes</p>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-xl border border-[#EBD08A] p-3 outline-none focus:border-[#FF1F8E]" placeholder="Quote amount, follow-ups…" data-testid="admin-order-notes" />
        <button onClick={() => patch({ notes })} className="btn-pink mt-3 !py-2.5" data-testid="admin-order-save-notes">Save notes</button>
      </div>
    </div>
  );
};

export default function AdminRequests() {
  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filter, setFilter] = useState("All");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    api.get("/meta").then((r) => setStatuses(r.data.statuses));
    api.get("/admin/orders").then((r) => { setOrders(r.data); setSel(r.data[0]?.id || null); });
  }, []);

  const shown = filter === "All" ? orders : orders.filter((o) => o.status === filter);
  const current = orders.find((o) => o.id === sel);
  const onSaved = (o) => setOrders(orders.map((x) => (x.id === o.id ? o : x)));

  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Order requests</h1>
      <div className="mt-5 flex gap-2 overflow-x-auto pb-2" data-testid="admin-status-filters">
        {["All", ...statuses].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold ${filter === s ? "bg-[#0E0B0D] text-white" : "bg-white ring-1 ring-[#EBD08A]"}`} data-testid={`admin-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}>
            {s} <span className="opacity-60">({s === "All" ? orders.length : orders.filter((o) => o.status === s).length})</span>
          </button>
        ))}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[360px_1fr]">
        <ul className="space-y-2" data-testid="admin-orders-list">
          {shown.map((o) => (
            <li key={o.id}>
              <button onClick={() => setSel(o.id)} className={`w-full rounded-2xl p-4 text-left transition-colors ${sel === o.id ? "bg-white ring-2 ring-[#FF1F8E]" : "bg-white/70 hover:bg-white"}`} data-testid={`admin-order-item-${o.id}`}>
                <div className="flex items-center justify-between gap-2"><span className="font-semibold">{o.name}</span><StatusBadge s={o.status} /></div>
                <p className="mt-1 text-sm text-black/60">{o.product_type} · Qty {o.quantity}</p>
                <p className="text-xs text-black/40">{new Date(o.created_at).toLocaleDateString()}</p>
              </button>
            </li>
          ))}
          {shown.length === 0 && <li className="rounded-2xl bg-white p-6 text-center text-sm text-black/50" data-testid="admin-orders-empty">No requests here yet.</li>}
        </ul>
        {current ? <Detail order={current} statuses={statuses} onSaved={onSaved} /> : <div className="rounded-3xl bg-white p-10 text-center text-black/50">Select a request to see details.</div>}
      </div>
    </div>
  );
}
